import serviceTypeCodes from 'constants/serviceTypeCodes';
import { toDecimalPlaces } from 'utils/calculator';
/**
 * Get the original price of the selected fare in the exchange screen
 * for each pax (if cancelled is false)
 * Get the price of the cancellation fare for each pax (if cancelled is true)
 */
export const getPricePerPax = ({ legs, passengerId, cancelled = false }) =>
  legs?.reduce(
    (acc, { products }) =>
      acc +
      products
        ?.filter((item) => item.passengerId === passengerId)
        ?.reduce((accPrice, { originalPrice, price }) => (accPrice + cancelled ? price : originalPrice), 0),
    0
  );

/**
 * Summated to Summated exchange
 *
 * Ultimately, we want to override the price of any cheaper ES or ER and never the SN leg.
 * However, train-inventory applies an automatic override price for every rebook with a cheaper
 * fare (see overridePriceForRebooking).
 *
 * Since it is automatically repriced if cheaper for all carriers, ES, ER or SN,
 * we need to bypass this here and reprice like so:
 * - To ES or ER cheaper => reprice to the price of the corresponding cancelled product
 * - To ES or ER more expensive => reprice to the price of the corresponding selected product
 * - To SN cheaper or more expensive => reprice to the price of the corresponding selected product
 *
 * This is necessary mainly to be backward compatible if the automatic reprice in train-inventory changes.
 * If this changes (connection team work) then this is not needed anymore
 *
 */
export const buildOverridePriceSummatedSummated = ({
  selectedLegInExchange,
  createdLegRebooked,
  cancellationLeg,
  carrier = serviceTypeCodes.EUROSTAR,
  shouldOverridePrice = false
}) => {
  const SNCreatedLegs = selectedLegInExchange?.filter(({ serviceType }) => serviceType.code === serviceTypeCodes.SNCF);
  const SNrebooked = createdLegRebooked?.filter(({ serviceType }) => serviceType.code === serviceTypeCodes.SNCF);
  const SNCancelledLegs = cancellationLeg?.filter(({ serviceType }) => serviceType.code === serviceTypeCodes.SNCF);

  const ESorERCreatedLegs = selectedLegInExchange?.filter(({ serviceType }) => serviceType.code === carrier);
  const ESorERCancelledLegs = cancellationLeg?.filter(({ serviceType }) => serviceType.code === carrier);
  const ESorERrebooked = createdLegRebooked?.filter(({ serviceType }) => serviceType.code === carrier);

  const priceSNForOverride = ({ passengerId }) =>
    shouldOverridePrice
      ? toDecimalPlaces(2, getPricePerPax({ legs: SNCancelledLegs, passengerId, cancelled: true }))
      : toDecimalPlaces(2, getPricePerPax({ legs: SNCreatedLegs, passengerId }));

  const overrideSN = SNrebooked.map(({ products }) =>
    products.map(({ itemRef, passengerId }) => ({
      itemRefId: Number(itemRef),
      price: priceSNForOverride({ passengerId })
    }))
  )?.flat();

  const overrideESorER = ESorERrebooked.map(({ products }) =>
    products.map(({ itemRef, passengerId }) => ({
      itemRefId: Number(itemRef),
      price: shouldOverridePrice
        ? toDecimalPlaces(2, getPricePerPax({ legs: ESorERCancelledLegs, passengerId, cancelled: true }))
        : getPricePerPax({ legs: ESorERCreatedLegs, passengerId }) >
          getPricePerPax({ legs: ESorERCancelledLegs, passengerId, cancelled: true })
        ? toDecimalPlaces(2, getPricePerPax({ legs: ESorERCreatedLegs, passengerId }))
        : toDecimalPlaces(2, getPricePerPax({ legs: ESorERCancelledLegs, passengerId, cancelled: true }))
    }))
  )?.flat();

  return [...overrideESorER, ...overrideSN]?.map(({ itemRefId, price }) => ({
    itemRefId,
    price
  }));
};

/**
 * Through to Summated exchange
 *
 * Ultimately, we want to override the price of the ES or ER leg only if the new total fares are cheaper
 * to not offer refunds in this case
 * However, train-inventory applies an automatic override price for every rebook with a cheaper
 * fare (see overridePriceForRebooking).
 *
 * The price of SN from through-fare to summated is always > 0 so the automatic reprice of train-inventory
 * does not trigger, therefore no need to override the SN legs here.
 *
 * The price of ES or ER from through-fare to summated can be < 0 so the automatic reprice of train-inventory
 * does trigger, therefore we need to override here and reprice the ES or ER leg like so:
 *
 * - if fare diff between cancelled and newly created is positive (i.e. exchanging to cheaper), override to ES or ER new fare + fare diff
 * - if fare diff between cancelled and newly created is negative (i.e. exchanging to more expensive), override to ES or ER new fare
 *
 *
 * This is necessary mainly to be backward compatible if the automatic reprice in train-inventory changes.
 * If this changes (connection team work) then this is not needed anymore
 *
 */
export const buildOverridePriceThroughSummated = ({
  selectedLegInExchange,
  cancellationLeg,
  createdLegRebooked,
  shouldOverridePrice = false,
  carrier = serviceTypeCodes.EUROSTAR
}) => {
  const ESorERCreatedLegs = selectedLegInExchange?.filter(({ serviceType }) => serviceType.code === carrier);
  const SNCreatedLegs = selectedLegInExchange?.filter(({ serviceType }) => serviceType.code === serviceTypeCodes.SNCF);
  const ESorERCancelledLegs = cancellationLeg?.filter(({ serviceType }) => serviceType.code === carrier);
  const ESorERrebooked = createdLegRebooked?.filter(({ serviceType }) => serviceType.code === carrier);
  const SNrebooked = createdLegRebooked?.filter(({ serviceType }) => serviceType.code === serviceTypeCodes.SNCF);

  const overrideESorER = ESorERrebooked.map(({ products }) =>
    products.map(({ itemRef, passengerId }) => {
      const ESorERNewPrice = getPricePerPax({ legs: ESorERCreatedLegs, passengerId });
      const ESorERCancelledPrice = getPricePerPax({ legs: ESorERCancelledLegs, passengerId, cancelled: true });
      const SNNewPrice = getPricePerPax({ legs: SNCreatedLegs, passengerId });
      const priceDiffCancelledCreated = ESorERCancelledPrice - (ESorERNewPrice + SNNewPrice);
      const SNRebookedWithOverride = getPricePerPax({ legs: SNrebooked, passengerId });

      return {
        itemRefId: Number(itemRef),
        price: shouldOverridePrice
          ? toDecimalPlaces(2, ESorERCancelledPrice - SNRebookedWithOverride)
          : priceDiffCancelledCreated > 0
          ? toDecimalPlaces(2, ESorERCancelledPrice - SNNewPrice)
          : toDecimalPlaces(2, ESorERNewPrice)
      };
    })
  )?.flat();

  return overrideESorER.map(({ itemRefId, price }) => ({
    itemRefId,
    price
  }));
};

export const overridePriceRequestSN = ({
  cancellationLeg,
  createdLeg,
  createdLegRebooked,
  isExchangingSummatedFare,
  isExchangingThroughFare,
  isExchangingToSummatedFare,
  carrier,
  shouldOverridePrice = false
}) => {
  if (isExchangingSummatedFare) {
    return isExchangingToSummatedFare
      ? buildOverridePriceSummatedSummated({
          selectedLegInExchange: createdLeg?.legs,
          createdLegRebooked,
          cancellationLeg,
          carrier,
          shouldOverridePrice
        })
      : [];
  }

  if (isExchangingThroughFare) {
    return isExchangingToSummatedFare
      ? buildOverridePriceThroughSummated({
          selectedLegInExchange: createdLeg?.legs,
          createdLegRebooked,
          cancellationLeg,
          carrier,
          shouldOverridePrice
        })
      : [];
  }

  return [];
};
